import { Box, Button, Tooltip } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useState } from 'react';
import { ItemInterface, ReactSortable } from 'react-sortablejs';

import type { BulletinMeta } from 'apis/bulletinboards';

import { MAX_ALLOWED_BULLETINS } from '../../../hooks/useBulletins';

import { BulletinListItem } from './bulletinListItem';

type BulletinListProps = {
  /** Are the bulletin's updating */
  isUpdating: boolean;
  /** This array of Bulletins */
  bulletins: readonly BulletinMeta[];
  /** Triggers when the "Add Bulletin" button is called */
  onAdd: () => void;
  /** The add bulletin button will be disabled when this is true */
  canAddMore: boolean;
  /**
   * Triggers when a bulletin is toggled enabled or disabled.
   *
   * @param bulletin The bulletin that had it's enabled flag changed.
   * @param enabled The new `enabled` setting.
   */
  setEnabled: (bulletin: BulletinMeta, enabled: boolean) => void;
  /**
   * Function to call when a bulletin is deleted
   *
   * @param bulletin The bulletin that triggered the delete.
   */
  onDelete: (bulletin: BulletinMeta) => void;
  /**
   * Triggered when a bulletin is edited.
   *
   * @parm bulletin the bulletin that triggered the edit.
   */
  onEdit: (bulletin: BulletinMeta) => void;
  /**
   * Triggered when the bulletins are reordered.
   *
   * @param bulletins The newly ordered array of bulletins
   */
  onReorder: (bulletins: BulletinMeta[]) => void;
};

type DraggableBulletins = BulletinMeta & ItemInterface;

export function BulletinList({
  isUpdating,
  onAdd,
  bulletins,
  canAddMore,
  setEnabled,
  onDelete,
  onEdit,
  onReorder,
}: BulletinListProps): JSX.Element {
  const [sortableBulletins, setSortableBulletins] = useState<
    DraggableBulletins[]
  >(bulletins.map((bulletin) => ({ ...bulletin })));

  // bulletins is an array of frozen objects which can cause issues with react-sortablejs because it tries to
  // mutate them. So we pass a non-frozen copy to it.
  useEffect(() => {
    setSortableBulletins(bulletins.map((bulletin) => ({ ...bulletin })));
  }, [bulletins]);

  const handleReorder = useCallback(() => {
    onReorder(
      sortableBulletins.map((bulletin) => {
        // filter out sortableJS extra props
        delete bulletin.chosen;
        delete bulletin.selected;
        delete bulletin.filtered;
        return bulletin;
      }),
    );
  }, [onReorder, sortableBulletins]);

  return (
    <Box padding="400" borderRadius="4px" borderColor="neutral.200">
      <ReactSortable
        disabled={isUpdating}
        list={sortableBulletins}
        setList={setSortableBulletins}
        onEnd={handleReorder}
        handle=".handle"
      >
        {sortableBulletins.map((bulletin) => (
          <BulletinListItem
            isUpdating={isUpdating}
            isDragging={!!bulletin.chosen}
            key={bulletin.id}
            bulletin={bulletin}
            setEnabled={setEnabled}
            onDelete={onDelete}
            onEdit={onEdit}
          />
        ))}
      </ReactSortable>

      <Tooltip
        disabled={canAddMore}
        content={`There is a maximum of ${MAX_ALLOWED_BULLETINS} bulletins allowed`}
        dark
      >
        <span>
          <Button
            disabled={isUpdating || !canAddMore}
            onClick={onAdd}
            variant="secondary"
          >
            Add content
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}
